import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle, environment, environments, switchEnvironment } = useAuth();

  const getErrorMessage = (err) => {
    const message = err.message || '';
    
    if (message.includes('auth/user-not-found') || message.includes('EMAIL_NOT_FOUND')) {
      return 'No account found with this email';
    }
    if (message.includes('auth/wrong-password') || message.includes('INVALID_PASSWORD') || message.includes('INVALID_LOGIN_CREDENTIALS')) {
      return 'Incorrect password';
    }
    if (message.includes('auth/invalid-email') || message.includes('INVALID_EMAIL')) {
      return 'Invalid email address';
    }
    if (message.includes('auth/too-many-requests') || message.includes('TOO_MANY_ATTEMPTS')) {
      return 'Too many failed attempts. Please try again later';
    }
    if (message.includes('auth/user-disabled') || message.includes('USER_DISABLED')) {
      return 'This account has been disabled';
    }
    if (message.includes('auth/popup-closed-by-user')) {
      return 'Login popup was closed';
    }
    
    return message || 'Login failed. Please try again.';
  };

  const handleSubmit = async () => {
    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      console.error('Login error:', err);
      setError(getErrorMessage(err));
    }
    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      await loginWithGoogle();
    } catch (err) {
      console.error('Google login error:', err);
      setError(getErrorMessage(err));
    }
    setLoading(false);
  };

  return (
    <div className="login-container">
      {/* Left Side - Branding */}
      <div className="login-branding">
        <div className="branding-content">
          <h1 className="brand-title">
            <span className="brand-qarl">QARL</span>
            <span className="brand-studio">Studio Dashboard</span>
          </h1>
          <p className="brand-subtitle">Internal User Only.</p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="login-form-section">
        <div className="login-form-container">
          <h2 className="form-title">Welcome back</h2>
          <p className="form-subtitle">Sign in to continue to your account</p>

          {error && <div className="login-error">{error}</div>}

          {/* Social Login Buttons */}
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="social-btn google-btn"
          >
            <svg className="social-icon" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </button>

          <div className="divider">
            <span className="divider-text">OR</span>
          </div>

          {/* Email & Password */}
          <div className="form-group">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              className="form-input"
              placeholder="Email *"
            />
          </div>

          <div className="form-group">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              className="form-input"
              placeholder="Password *"
            />
          </div>

          <div className="forgot-password">
            <a href="#forgot" className="forgot-link">Forgot password?</a>
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="signin-btn"
          >
            <svg className="signin-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M13.8 12H3"/>
            </svg>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <p className="signup-prompt">
            Don't have an account? <a href="#signup" className="signup-link">Create an account</a>
          </p>

          <div className="login-env-selector">
            <label className="login-env-label">Environment</label>
            <div className="login-env-options">
              {Object.entries(environments).map(([key, env]) => (
                <button
                  key={key}
                  onClick={() => switchEnvironment(key)}
                  className={`login-env-btn ${environment === key ? 'login-env-btn-active' : ''}`}
                >
                  {env.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <span className="login-version">v{process.env.REACT_APP_VERSION || '0.0.0'}</span>
    </div>
  );
};

export default Login;